import React from "react";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";
import * as _ from "lodash";

import { PageProps } from "src/pages/Router/routes";
import useTimezone from "src/core/shared/hooks/time-helpers/useTimeZone";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  IDeliveryWeekLeadTimesSetting,
  IInheritedSetting
} from "src/core/drops/api/drops.api";
import {
  IProductLeadTimeGroup,
  useProductsApi
} from "src/core/products/api/products.api";
import {
  GetSettingsParams,
  UpdateSettingParams,
  useInheritedSettingsApi
} from "src/core/shared/api/inheritedSettings.api";
import { completeLeadTimeConfiguration } from "src/core/orders/utils/order-deliveries";
import {
  Label,
  Select
} from "src/library/components/Forms";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Tooltip } from "src/library/components/Tooltip";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import { StaticTable } from "src/library/components/Tables";
import { Button } from "src/library/components/Button";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";

import { CutoffTimeSelection } from "../CutoffTimeSelection";
import { useLeadTimeDayRow } from "../../hooks/useLeadTimeDayRow";

export const LeadTimeSettingsTabGlobal: React.ReactFCWithChildren<PageProps<unknown>> = () => {
  const [ currentLeadTimeGroup, setCurrentLeadTimeGroup ] = React.useState<IProductLeadTimeGroup>();
  const timezone = useTimezone();
  const checkPermission = useCheckUserPermission();
  const canAmend = checkPermission("amend-global-settings");
  // When mounting the component, request the required data:
  // - Existing lead times.
  // - Existing lead times configuration.
  // - Existing default cut-off configuration.
  const [ existingLeadTimes, setExistingLeadTimes ] = React.useState<null | IProductLeadTimeGroup[]>(null);
  const [ leadTimeConfiguration, setLeadTimeConfiguration ] = React.useState<null | IInheritedSetting<"delivery_week_leadtimes">>(null);
  const [ defaultCutoff, setDefaultCutoff ] = React.useState<null | IInheritedSetting<"delivery_cutoff">>(null);
  // Current lead time configuration will contain current setting values.
  // The values are completed to make sure new lead times are added and it
  // up to date with the current user changes.
  const [ currentLeadTimeConfiguration, setCurrentLeadTimeConfiguration ] = React.useState<null | IDeliveryWeekLeadTimesSetting>(null);
  const [ getLeadTimesResponse, getLeadTimes ] = useProductsApi("GET_ALL_LEAD_TIMES");
  const [ getSettingsResponse, getSetting ] = useInheritedSettingsApi<"GET_GLOBAL_SETTINGS", GetSettingsParams, Array<IInheritedSetting<"delivery_week_leadtimes" | "delivery_cutoff">>>("GET_GLOBAL_SETTINGS");
  // Handles settings update.
  const [ updateLeadTimesResponse, updateLeadTimes ] = useInheritedSettingsApi<"UPDATE_SETTING", UpdateSettingParams, IInheritedSetting<"delivery_week_leadtimes">>("UPDATE_SETTING");
  const [ updateDefaultCutoffResponse, updateDefaultCutoff ] = useInheritedSettingsApi<"UPDATE_SETTING", UpdateSettingParams, IInheritedSetting<"delivery_cutoff">>("UPDATE_SETTING");
  const [ updatingSettings, setUpdatingSettings ] = React.useState<boolean>(false);
  const [ changesInSettings, setChangesInSettings ] = React.useState<boolean>(false);

  const leadTimeDays = useLeadTimeDayRow({
    currentLeadTimeGroup,
    currentLeadTimeConfiguration,
    disabled: !canAmend,
    defaultCutoff: defaultCutoff ? defaultCutoff.value : null,
    setCurrentLeadTimeConfiguration: configuration => {
      setCurrentLeadTimeConfiguration(configuration);
      setChangesInSettings(true);
    }
  });

  React.useEffect(() => {
    if (!existingLeadTimes) {
      getLeadTimes(null);
    }

    if (!leadTimeConfiguration) {
      getSetting({ name: [ "delivery_week_leadtimes", "delivery_cutoff" ] });
    }
    // getLeatTimes using old api structure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    existingLeadTimes,
    getSetting,
    leadTimeConfiguration
  ]);

  // Handles GET lead times response.
  React.useEffect(() => {
    if (getLeadTimesResponse.data) {
      setExistingLeadTimes(getLeadTimesResponse.data);

      // Set the current lead time configuration if:
      // - it is not already loaded
      // - the other required field is loaded (leadTimeConfiguration).
      if (leadTimeConfiguration && !currentLeadTimeConfiguration) {
        setCurrentLeadTimeConfiguration(completeLeadTimeConfiguration(getLeadTimesResponse.data, leadTimeConfiguration.value));
      }

      // Make sure the default lead time is loaded if no
      // query parameter is found.
      if (currentLeadTimeGroup === undefined) {
        setCurrentLeadTimeGroup(getLeadTimesResponse.data[ 0 ]);
      }
    }

    if (getLeadTimesResponse.error) {
      toast.error("There was an error loading the lead time configuration.");
    }
  }, [
    currentLeadTimeConfiguration,
    currentLeadTimeGroup,
    getLeadTimesResponse,
    leadTimeConfiguration
  ]);

  // Handles GET setting response.
  React.useEffect(() => {
    if (getSettingsResponse.data) {
      const initialConfiguration = _.find(getSettingsResponse.data, setting => setting.name === "delivery_week_leadtimes") as IInheritedSetting<"delivery_week_leadtimes"> | undefined;
      const initialDefaultCutoff = _.find(getSettingsResponse.data, setting => setting.name === "delivery_cutoff") as IInheritedSetting<"delivery_cutoff"> | undefined;

      if (initialDefaultCutoff && !defaultCutoff) {
        setDefaultCutoff(initialDefaultCutoff);
      }

      if (initialConfiguration && defaultCutoff) {
        setLeadTimeConfiguration(initialConfiguration);

        // Set the current lead time configuration if the other required field is loaded (existingLeadTimes).
        if (existingLeadTimes) {
          const initialLeadTimes = completeLeadTimeConfiguration(existingLeadTimes, initialConfiguration.value);

          setCurrentLeadTimeConfiguration(initialLeadTimes);
        }
      }
    }

    if (getSettingsResponse.error) {
      toast.error("There was an error loading the lead time configuration.");
    }
  }, [
    existingLeadTimes,
    getSettingsResponse,
    defaultCutoff
  ]);

  // handle responses to only get 1 toast error
  React.useEffect(() => {
    if (!updateLeadTimesResponse.pending && !updateDefaultCutoffResponse.pending) {
      if (updateLeadTimesResponse.error) {
        toast.error("Could not save settings.");
        setUpdatingSettings(false);
      } else if (updateDefaultCutoffResponse.error) {
        toast.error("Could not save settings.");
        setUpdatingSettings(false);
      } else if (updateLeadTimesResponse.data && updateDefaultCutoffResponse.data) {
        toast.success("Setting were correctly saved.");
        setChangesInSettings(false);
        setUpdatingSettings(false);
      }
    }
  }, [
    updateLeadTimesResponse,
    updateDefaultCutoffResponse.pending,
    updateDefaultCutoffResponse
  ]);

  // change the default cutoff time
  const changeDefaultCutoff = React.useCallback((time: string) => {
    if (currentLeadTimeConfiguration && defaultCutoff) {
      setChangesInSettings(true);
      const timeRegex = new RegExp(defaultCutoff.value.time, "g");
      const replaceDefaults = JSON.stringify(currentLeadTimeConfiguration).replace(timeRegex, time);

      setCurrentLeadTimeConfiguration(JSON.parse(replaceDefaults));

      setDefaultCutoff({
        ...defaultCutoff,
        value: { time }
      });
    }
  }, [ defaultCutoff, currentLeadTimeConfiguration ]);

  return (
    <LeadTimeSettingsTabGlobalWrapper>
      {defaultCutoff && (
        <div className="default-cutoff-contianer">
          <div
            className="label-wrapper"
            data-tip
            data-for="cutoff-tooltip"
          >
            <Label
              for="default-cutoff"
              label="Default cutoff time"
            />

            <Icon
              icon={Icons.HELP}
              size={10}
            />

            {" "}

            <Tooltip
              id="cutoff-tooltip"
              type="dark"
              place="top"
              getContent={() => {
                return (
                  <Text color="white">
                    The default cutoff time is used where no override value has been set in the lead times table below
                  </Text>
                );
              }}
            />
          </div>

          <CutoffTimeSelection
            disabled={!canAmend}
            setTime={changeDefaultCutoff}
            value={defaultCutoff.value.time}
            name="default-cutoff"
            timezone={timezone}
          />
        </div>
      )}

      {existingLeadTimes && currentLeadTimeConfiguration && currentLeadTimeGroup ? (
        <>
          <br />

          <Text
            as="p"
            fontSize="h4"
          >
            Lead Time
          </Text>

          <Select
            as="select"
            label="Lead Times"
            autoHideLabels
            className="lead-times-select"
            name="lead-times-options"
            value={currentLeadTimeGroup.name}
            handleChange={e => {
              const newLeadTimeGroup = _.find(existingLeadTimes, ltg => ltg.name === e.target.value);

              setCurrentLeadTimeGroup(newLeadTimeGroup);
            }}
            options={_.map(existingLeadTimes, leadtime => ({
              text: `${leadtime.name} - ${leadtime.defaultLeadTimeDays} day${leadtime.defaultLeadTimeDays > 1 ? "s" : ""}`,
              value: leadtime.name
            }))}
          />

          <Grid.Bounds>
            <Grid.Box width="100%">
              {leadTimeDays && (
                <StaticTable
                  headers={[
                    "Week day",
                    "Cut Off day",
                    "Cut Off week",
                    "Cut off time"
                  ]}
                  centerCellContent={false}
                  bodyRows={leadTimeDays}
                />
              )}
            </Grid.Box>
          </Grid.Bounds>

          <Button
            loading={updatingSettings}
            className="save-changes"
            disabled={!canAmend || !changesInSettings}
            $buttonType="primary"
            onClick={() => {
              setUpdatingSettings(true);
              const removeCutoffTimesRegex = new RegExp(`,"cutoffTime":"${(defaultCutoff as IInheritedSetting<"delivery_cutoff">).value.time}"`, "g");

              updateLeadTimes({
                ...leadTimeConfiguration,
                value: JSON.stringify(currentLeadTimeConfiguration).replace(removeCutoffTimesRegex, "")
              } as UpdateSettingParams);
              updateDefaultCutoff(defaultCutoff as UpdateSettingParams);
            }}
          >
            Save changes
          </Button>
        </>
      ) : (
        <div className="spinner-container">
          <LoadingSpinner
            type="circular"
            position="inline"
          />
        </div>
      )}
    </LeadTimeSettingsTabGlobalWrapper>
  );
};

const LeadTimeSettingsTabGlobalWrapper = styled.div`
  ${({ theme }) => css`
    .default-cutoff-contianer {
      margin-bottom: ${theme.spacing.margins.xl};

      .label-wrapper {
        display: flex;
      }
    }

    .lead-times-select {
      margin-bottom: ${theme.spacing.margins.xl};
    }

    .save-changes {
      margin-top: ${theme.spacing.margins.xl};
    }
    
    .spinner-container {
      display: flex;
      justify-content: center;
      text-align: center;
    }
  `}
`;
